'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { useAuth } from '@/contexts/AuthContext';

export default function HeroV3() {
  const { isAuthenticated } = useAuth();
  const [beforeAfterPosition, setBeforeAfterPosition] = useState(50);

  return (
    <section className="relative min-h-[750px] lg:min-h-[820px] flex items-center overflow-x-hidden bg-[#0B0D17] pb-20 lg:pb-24">
      {/* Background with radial glow - with breathing animation and larger spread */}
      <div className="absolute inset-0">
        <div className="absolute top-1/4 left-1/3 w-[1200px] h-[1200px] bg-purple-600/10 rounded-full blur-[160px] animate-glow-breathe" />
        <div className="absolute bottom-1/4 right-1/3 w-[900px] h-[900px] bg-blue-600/5 rounded-full blur-[140px] animate-glow-breathe-delayed" />
        <div 
          className="absolute inset-0 opacity-[0.025]"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
          }}
        />
      </div>

      <div className="relative z-10 w-full max-w-[1920px] mx-auto px-6 lg:px-12 xl:px-16 2xl:px-20 py-2 lg:py-3 xl:py-4">
        <div className="grid lg:grid-cols-[42%_58%] gap-8 xl:gap-12 2xl:gap-16 items-start">
          
          {/* Left Column - Better vertical positioning */}
          <div className="space-y-6 min-w-0 lg:mt-2">
            {/* Delivery speed badge */}
            <div className="inline-flex items-center gap-2 pl-2 pr-3.5 py-1.5 rounded-full bg-white/[0.04] border border-white/[0.12] backdrop-blur-sm">
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gradient-to-r from-purple-600 to-purple-500 text-white text-xs font-bold">
                <span>⚡</span> As fast as 4 hours
              </span>
              <span className="text-xs font-medium text-gray-400">Average delivery</span>
            </div>

            {/* Headline with reduced spacing below */}
            <h1 className="text-6xl lg:text-7xl xl:text-[5.25rem] font-bold leading-[0.98] tracking-[-0.02em]">
              <span className="text-white">Professional</span>
              <br />
              <span className="text-white">Real Estate</span>
              <br />
              <span className="text-white">Photo Editing.</span>
              <br />
              <span className="bg-gradient-to-r from-purple-400 via-purple-300 to-blue-400 bg-clip-text text-transparent">
                Delivered Fast.
              </span>
            </h1>

            {/* Description with reduced spacing */}
            <p className="text-xl text-gray-400 leading-relaxed max-w-xl pt-1">
              Upload your real estate photos, track every order in one dashboard, and receive professionally edited images within 4–12 hours.
            </p>

            {/* CTA Buttons with reduced spacing */}
            <div className="flex flex-col sm:flex-row gap-4 pt-2">
              <Link
                href={isAuthenticated ? '/image-upload-and-order-creation' : '/auth/signup'}
                className="group relative px-9 py-4 bg-gradient-to-r from-purple-600 to-purple-500 hover:from-purple-500 hover:to-purple-400 text-white text-base font-bold rounded-xl transition-all duration-300 flex items-center justify-center gap-2 shadow-xl shadow-purple-500/30 hover:shadow-purple-500/50 hover:-translate-y-1 hover:scale-[1.02]"
              >
                <span>Start Free Trial</span>
                <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </Link>
              
              <button className="group px-9 py-4 bg-white/[0.03] hover:bg-white/[0.08] backdrop-blur-sm text-white text-base font-semibold rounded-xl transition-all duration-300 flex items-center justify-center gap-2 border border-white/[0.15] hover:border-white/30 hover:-translate-y-0.5">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                </svg>
                <span>Explore Dashboard</span>
              </button>
            </div>

            {/* Statistics with reduced spacing */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 pt-6">
              {[
                { value: '10,000+', label: 'Images Edited', icon: '📸' },
                { value: '4 hrs', label: 'As Fast As', icon: '⚡' },
                { value: '4.9★', label: 'Rating', icon: '⭐' },
                { value: '99%', label: 'Satisfaction', icon: '💯' },
              ].map((stat) => (
                <div key={stat.label} className="space-y-1">
                  <div className="flex items-center gap-2">
                    <span className="text-lg">{stat.icon}</span>
                    <span className="text-2xl font-bold text-white">{stat.value}</span>
                  </div>
                  <div className="text-sm text-gray-500">{stat.label}</div>
                </div>
              ))}
            </div>

            {/* Trust message with reduced spacing */}
            <div className="pt-1">
              <p className="text-sm text-gray-500">
                <span className="text-gray-400 font-medium">No subscription required</span>
                <span className="mx-2">•</span>
                <span className="text-gray-400 font-medium">Pay only for what you use</span>
              </p>
            </div>

            {/* Trust Row - moved closer */}
            <div className="flex items-center gap-4 pt-2">
              <div className="flex -space-x-2">
                {[1, 2, 3, 4].map((i) => (
                  <div
                    key={i}
                    className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-400 to-blue-400 border-2 border-[#0B0D17] flex items-center justify-center text-white text-xs font-semibold"
                  >
                    {String.fromCharCode(64 + i)}
                  </div>
                ))}
              </div>
              <div className="flex flex-col">
                <div className="flex items-center gap-0.5">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <svg key={star} className="w-4 h-4 text-purple-400" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  ))}
                </div>
                <p className="text-sm text-gray-400 mt-1">
                  Trusted by 1,000+ Real Estate Professionals
                </p>
              </div>
            </div>
          </div>

          {/* Right Column - Dashboard Preview at full size so it dominates the hero */}
          <div className="relative lg:mt-0 w-full min-w-0 lg:pr-4 xl:pr-6 lg:scale-100 xl:scale-[1.04] 2xl:scale-[1.08] origin-top">
            <DashboardPreview 
              beforeAfterPosition={beforeAfterPosition}
              setBeforeAfterPosition={setBeforeAfterPosition}
            />
          </div>

        </div>
      </div>
    </section>
  );
}

// Dashboard Preview Component - REAL UI with actual data - BIGGER SIZE + INTERACTIVE
function DashboardPreview({ 
  beforeAfterPosition, 
  setBeforeAfterPosition 
}: { 
  beforeAfterPosition: number; 
  setBeforeAfterPosition: React.Dispatch<React.SetStateAction<number>>;
}) {
  const [isHovering, setIsHovering] = React.useState(false);
  const [isDragging, setIsDragging] = React.useState(false);
  const sliderRef = React.useRef<HTMLDivElement>(null);

  // Gentle auto-nudge on hover — paused while the user is dragging
  React.useEffect(() => {
    if (!isHovering || isDragging) return;

    const interval = setInterval(() => {
      setBeforeAfterPosition((prev) => {
        const target = 60; // Gentle movement toward 60%
        const diff = target - prev;
        if (Math.abs(diff) < 0.3) return target;
        return prev + diff * 0.12; // Smooth interpolation
      });
    }, 16);

    return () => clearInterval(interval);
  }, [isHovering, isDragging, setBeforeAfterPosition]);

  // Update the slider position from a pointer x-coordinate
  const updateFromClientX = React.useCallback(
    (clientX: number) => {
      const el = sliderRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const percentage = ((clientX - rect.left) / rect.width) * 100;
      setBeforeAfterPosition(Math.max(0, Math.min(100, percentage)));
    },
    [setBeforeAfterPosition]
  );

  const handlePointerDown = (e: React.PointerEvent) => {
    e.preventDefault();
    (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
    setIsDragging(true);
    updateFromClientX(e.clientX);
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    if (!isDragging) return;
    updateFromClientX(e.clientX);
  };

  const endDrag = () => setIsDragging(false);

  return (
    <div 
      className="relative w-full animate-float-slow overflow-hidden"
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => {
        setIsHovering(false);
        if (!isDragging) {
          setTimeout(() => {
            if (!isDragging) setBeforeAfterPosition(50);
          }, 300);
        }
      }}
    >
      {/* Purple glow behind dashboard - makes it pop with stronger shadow */}
      <div className="absolute -inset-4 bg-purple-600/15 rounded-2xl blur-[80px] -z-10" />
      
      {/* Main Dashboard Card - Real app window feel with stronger shadow */}
      <div className="relative bg-[#13151F]/95 backdrop-blur-2xl rounded-2xl border-2 border-white/[0.12] shadow-[0_30px_80px_rgba(0,0,0,0.6),0_10px_30px_rgba(124,58,237,0.15)] overflow-hidden transition-all duration-500 hover:border-purple-500/30 hover:shadow-[0_35px_90px_rgba(124,58,237,0.4)] w-full">
        
        {/* App Window Title Bar */}
        <div className="flex items-center justify-between px-4 py-2.5 bg-gradient-to-r from-white/[0.08] to-white/[0.04] border-b border-white/[0.08]">
          <div className="flex items-center gap-2">
            {/* Window control dots */}
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded-full bg-red-500/80" />
              <div className="w-3 h-3 rounded-full bg-yellow-500/80" />
              <div className="w-3 h-3 rounded-full bg-green-500/80" />
            </div>
          </div>
          <div className="flex items-center gap-2 text-xs text-gray-400 font-medium">
            <span>Client Dashboard</span>
          </div>
          <div className="w-16" /> {/* Spacer for balance */}
        </div>
        
        {/* Dashboard Header with Real Profile - More contextual */}
        <div className="flex items-center justify-between px-6 py-4 bg-gradient-to-r from-purple-500/[0.12] via-purple-500/[0.06] to-transparent border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center shadow-lg shadow-purple-500/30">
              <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z" />
              </svg>
            </div>
            <div>
              <span className="text-white font-semibold text-sm">Welcome back, Marcus</span>
              <div className="text-xs text-purple-400 font-medium">You have 3 orders in progress</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button className="relative p-2 hover:bg-white/5 rounded-lg transition-colors">
              <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
              </svg>
              <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-purple-500 rounded-full animate-pulse"></span>
            </button>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-400 to-pink-400 flex items-center justify-center text-white text-xs font-bold">
                SJ
              </div>
            </div>
          </div>
        </div>

        {/* Stats Cards - Real Data */}
        <div className="grid grid-cols-4 gap-3 p-6 bg-gradient-to-b from-purple-500/5 to-transparent">
          {[
            { label: 'Total Orders', value: '128', icon: '📦', change: '+12%', trend: 'up' },
            { label: 'In Progress', value: '45', icon: '⏳', change: '+32%', trend: 'up' },
            { label: 'Completed', value: '83', icon: '✅', change: '+15%', trend: 'up' },
            { label: 'Total Spent', value: '$1,248', icon: '💰', change: '+8%', trend: 'up' },
          ].map((stat, idx) => (
            <div key={idx} className="group bg-white/[0.04] hover:bg-white/[0.08] backdrop-blur-sm rounded-xl p-4 border border-white/[0.08] hover:border-white/[0.15] transition-all duration-300 cursor-pointer">
              <div className="flex items-center justify-between mb-2">
                <span className="text-lg">{stat.icon}</span>
                <span className="text-xs text-green-400 bg-green-400/10 px-2 py-0.5 rounded-full font-medium">{stat.change}</span>
              </div>
              <div className="text-2xl font-bold text-white mb-0.5">{stat.value}</div>
              <div className="text-xs text-gray-400">{stat.label}</div>
            </div>
          ))}
        </div>

        {/* Before/After Preview Card with REAL Image */}
        <div className="px-6 pb-5">
          <div className="bg-white/[0.04] backdrop-blur-sm rounded-xl p-4 border border-white/[0.08] hover:border-purple-500/30 transition-all duration-300 group">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-white">Featured Project</span>
                <span className="text-xs text-purple-400">ORD-7763</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-green-400 bg-green-400/10 px-2.5 py-1 rounded-full font-medium flex items-center gap-1">
                  <span className="w-1.5 h-1.5 bg-green-400 rounded-full animate-pulse"></span>
                  Ready for Download
                </span>
              </div>
            </div>
            
            {/* Before/After Slider with Real Property Images */}
            <div
              ref={sliderRef}
              className="relative aspect-[16/10] rounded-lg overflow-hidden bg-gray-900 shadow-lg select-none touch-none cursor-ew-resize"
              onPointerDown={handlePointerDown}
              onPointerMove={handlePointerMove}
              onPointerUp={endDrag}
              onPointerCancel={endDrag}
            >
              {/* Before Image */}
              <div 
                className="absolute inset-0 pointer-events-none"
                style={{
                  backgroundImage: `url('/room-before.jpg')`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                }}
              >
                <div className="absolute top-3 left-3 px-2.5 py-1 bg-black/70 backdrop-blur-sm rounded-md text-white text-xs font-semibold">
                  Before
                </div>
              </div>
              
              {/* After Image with clip - Enhanced/edited version */}
              <div 
                className="absolute inset-0 pointer-events-none will-change-[clip-path]"
                style={{
                  backgroundImage: `url('/room-after.jpg')`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  clipPath: `polygon(${beforeAfterPosition}% 0, 100% 0, 100% 100%, ${beforeAfterPosition}% 100%)`,
                  transition: isDragging ? 'none' : 'clip-path 120ms ease-out',
                }}
              >
                <div className="absolute top-3 right-3 px-2.5 py-1 bg-purple-600/80 backdrop-blur-sm rounded-md text-white text-xs font-semibold flex items-center gap-1">
                  <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                  After
                </div>
              </div>

              {/* Slider Handle */}
              <div 
                className="absolute top-0 bottom-0 w-0.5 bg-white z-10 pointer-events-none will-change-[left]"
                style={{
                  left: `${beforeAfterPosition}%`,
                  transform: 'translateX(-50%)',
                  transition: isDragging ? 'none' : 'left 120ms ease-out',
                }}
              >
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 bg-white rounded-full shadow-xl flex items-center justify-center">
                  <svg className="w-5 h-5 text-gray-900" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M8 9l4-4 4 4m0 6l-4 4-4-4" />
                  </svg>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between mt-3">
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500">Completed 2 hours ago</span>
                <span className="text-xs text-gray-600">•</span>
                <span className="text-xs text-gray-400">HDR Processing • 4 images</span>
              </div>
              <button className="text-xs text-white bg-purple-600 hover:bg-purple-500 px-3 py-1.5 rounded-lg font-semibold flex items-center gap-1.5 transition-all group-hover:scale-105">
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                Download
              </button>
            </div>
          </div>
        </div>

        {/* Today's Orders - Changed from Recent Orders, with bottom fade */}
        <div className="px-6 pb-6 relative">
          <div className="flex items-center justify-between mb-3">
            <div>
              <span className="text-sm font-semibold text-white">Today's Orders</span>
              <span className="text-xs text-gray-500 ml-2">3 active</span>
            </div>
            <button className="text-xs text-purple-400 hover:text-purple-300 font-medium">View All →</button>
          </div>
          <div className="space-y-2">
            {[
              { id: 'ORD-7764', service: 'HDR Processing', images: 6, status: 'Completed', action: 'Download', statusColor: 'green', date: 'Today' },
              { id: 'ORD-7765', service: 'Day to Dusk', images: 3, status: 'In Progress', action: 'Preview', statusColor: 'blue', date: 'Today' },
              { id: 'ORD-7766', service: 'Virtual Staging', images: 2, status: 'Ready', action: 'Track', statusColor: 'purple', date: 'Today' },
            ].map((order, idx) => (
              <div key={order.id} className={`flex items-center justify-between bg-white/[0.04] hover:bg-white/[0.08] backdrop-blur-sm rounded-lg p-3 border border-white/[0.08] hover:border-white/[0.15] transition-all duration-300 cursor-pointer group ${idx === 2 ? 'opacity-60' : ''}`}>
                <div className="flex items-center gap-3 flex-1">
                  <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-gray-700 to-gray-800 overflow-hidden flex-shrink-0">
                    <div 
                      className="w-full h-full bg-cover bg-center"
                      style={{
                        backgroundImage: order.id === 'ORD-7764' 
                          ? `url('https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=200&h=200&fit=crop&q=80')`
                          : order.id === 'ORD-7765'
                          ? `url('https://images.unsplash.com/photo-1600566753190-17f0baa2a6c3?w=200&h=200&fit=crop&q=80')`
                          : `url('https://images.unsplash.com/photo-1600573472550-8090b5e0745e?w=200&h=200&fit=crop&q=80')`
                      }}
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <div className="text-sm font-semibold text-white">{order.id}</div>
                      <div className="text-xs text-gray-500">•</div>
                      <div className="text-xs text-gray-400">{order.date}</div>
                    </div>
                    <div className="text-xs text-gray-400 mt-0.5">{order.service} • {order.images} images</div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`text-xs px-2.5 py-1 rounded-full font-medium whitespace-nowrap ${
                    order.statusColor === 'green' 
                      ? 'bg-green-400/10 text-green-400' 
                      : order.statusColor === 'blue'
                      ? 'bg-blue-400/10 text-blue-400'
                      : 'bg-purple-400/10 text-purple-400'
                  }`}>
                    {order.status}
                  </span>
                  <button className="opacity-0 group-hover:opacity-100 px-3 py-1.5 bg-purple-600 hover:bg-purple-500 text-white text-xs font-medium rounded-lg transition-all">
                    {order.action}
                  </button>
                </div>
              </div>
            ))}
          </div>
          
          {/* Subtle fade at bottom to show content continues */}
          <div className="absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-[#13151F] to-transparent pointer-events-none" />
        </div>
      </div>

      {/* Floating Status Cards - Repositioned as decorative accents */}
      <FloatingCards />
    </div>
  );
}

// Floating Status Cards Component - Only ONE card with enhanced animation
function FloatingCards() {
  return (
    <div
      className="hidden xl:flex absolute -top-6 -left-32 items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-green-500/20 to-green-600/10 backdrop-blur-lg rounded-xl border border-white/20 shadow-2xl animate-float-badge hover:scale-105 transition-transform cursor-default"
    >
      <span className="text-xl animate-pulse">✓</span>
      <span className="text-sm font-semibold text-white whitespace-nowrap">HDR Completed</span>
    </div>
  );
}

{/* Add CSS animations */}
<style jsx global>{`
  @keyframes float-slow {
    0%, 100% {
      transform: translateY(0px);
    }
    50% {
      transform: translateY(-12px);
    }
  }

  @keyframes float-slow-delayed {
    0%, 100% {
      transform: translateY(0px);
    }
    50% {
      transform: translateY(-10px);
    }
  }

  @keyframes float-badge {
    0%, 100% {
      transform: translateY(0px) translateX(0px);
    }
    25% {
      transform: translateY(-8px) translateX(4px);
    }
    75% {
      transform: translateY(-12px) translateX(-4px);
    }
  }

  @keyframes glow-breathe {
    0%, 100% {
      opacity: 0.1;
      transform: scale(1);
    }
    50% {
      opacity: 0.15;
      transform: scale(1.05);
    }
  }

  @keyframes glow-breathe-delayed {
    0%, 100% {
      opacity: 0.05;
      transform: scale(1);
    }
    50% {
      opacity: 0.08;
      transform: scale(1.03);
    }
  }

  .animate-float-slow {
    animation: float-slow 8s ease-in-out infinite;
  }

  .animate-float-slow-delayed {
    animation: float-slow-delayed 6s ease-in-out infinite;
    animation-delay: 1s;
  }

  .animate-float-badge {
    animation: float-badge 5s ease-in-out infinite;
    animation-delay: 0.5s;
  }

  .animate-glow-breathe {
    animation: glow-breathe 8s ease-in-out infinite;
  }

  .animate-glow-breathe-delayed {
    animation: glow-breathe-delayed 10s ease-in-out infinite;
    animation-delay: 2s;
  }
`}</style>
